import { takeUntil } from "rxjs/operators";

const ENTERPRISE_COLORS = {
  green: { hue: 130, sat: 65 },
  yellow: { hue: 45, sat: 70 },
  red: { hue: 0, sat: 65 },
  orange: { hue: 25, sat: 70 },
  blue: { hue: 210, sat: 65 },
  purple: { hue: 275, sat: 60 },
  grey: { hue: 0, sat: 0 },
};

interface SemanticRule {
  group: string;
  color: keyof typeof ENTERPRISE_COLORS;
  lightness: number;
}

const SEMANTIC_RULES: Record<string, SemanticRule> = {
  // sentiment
  yes: { group: "sentiment", color: "green", lightness: 40 },
  no: { group: "sentiment", color: "red", lightness: 50 },
  good: { group: "sentiment", color: "green", lightness: 40 },
  maybe: { group: "sentiment", color: "yellow", lightness: 50 },
  bad: { group: "sentiment", color: "red", lightness: 50 },

  // status
  completed: { group: "status", color: "green", lightness: 40 },
  inprogress: { group: "status", color: "blue", lightness: 50 },
  pending: { group: "status", color: "yellow", lightness: 55 },
  blocked: { group: "status", color: "red", lightness: 40 },

  // risk
  critical: { group: "risk", color: "red", lightness: 40 },
  high: { group: "risk", color: "orange", lightness: 45 },
  medium: { group: "risk", color: "yellow", lightness: 50 },
  low: { group: "risk", color: "green", lightness: 40 },

  // compliance
  compliant: { group: "compliance", color: "green", lightness: 40 },
  partial: { group: "compliance", color: "yellow", lightness: 55 },
  noncompliant: { group: "compliance", color: "red", lightness: 40 },
  notassessed: { group: "compliance", color: "blue", lightness: 65 },
  na: { group: "compliance", color: "grey", lightness: 80 },

  // confidence
  unknown: { group: "confidence", color: "grey", lightness: 75 },
  disputed: { group: "confidence", color: "purple", lightness: 50 },

  // trend
  improving: { group: "trend", color: "green", lightness: 40 },
  stable: { group: "trend", color: "blue", lightness: 55 },
  declining: { group: "trend", color: "red", lightness: 45 },
};

function resolveEnterpriseColor(label: string): string {
  const key = Object.keys(SEMANTIC_RULES).find((k) =>
    label.toLowerCase().includes(k)
  );

  if (!key) return "";

  const rule = SEMANTIC_RULES[key];
  const base = ENTERPRISE_COLORS[rule.color];

  return `hsl(${base.hue}, ${base.sat}%, ${rule.lightness}%)`;
}

function generateFallbackColor(label: string): string {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = label.charCodeAt(i) + ((hash << 5) - hash);
  }

  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 60%, 50%)`;
}

export function handleGovAggregateResp(this: any) {
  this.additionalInfoLoaded = false;
  this.governanceLighthouseService
    .getAggregateResponseDetails(this.currentGovAssessment)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((response: any) => {
      this.additionalInfoLoaded = true;
      this.govAggregateData = response.group_wise_data;
      this.govAggregateData.forEach((group: any) => {
        group.questions.forEach((question: any) => {
          question["chart"] = getChartData(question.display_score_data);
        });
      });
    });
}

export function handleAssessmentAggregateResp(this: any) {
  this.additionalInfoLoaded = false;
  this.assessmentCentralService
    .getAggregateResponseDetails(this.currentAssessment)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((response: any) => {
      this.additionalInfoLoaded = true;
      this.assessmentAggregateData = response.group_wise_data;
      this.assessmentAggregateData.forEach((group: any) => {
        group.questions.forEach((question: any) => {
          question["chart"] = getChartData(question.display_score_data);
        });
      });
    });
}

export function handleAuditInstance(this: any) {
  this.additionalInfoLoaded = false;
  this.incidentTrackerService
    .getSeverityReport(this.currentAudit)
    .pipe(takeUntil(this.unsubscribe$))
    .subscribe((resp: any) => {
      this.additionalInfoLoaded = true;
      this.auditInstanceInfo = resp;
      this.auditInstanceInfo.forEach((item: any) => {
        item["chart"] = getChartData(item.analytics, true);
      });
    });
}

export function getChartData(responses: any, audits = false) {
  const xAxis = Object.keys(responses);
  const seriesData: any = [];

  for (const item of xAxis) {
    let color = resolveEnterpriseColor(item);

    if (!color) {
      color = generateFallbackColor(item);
    }

    const obj = {
      name: item,
      data: [responses[item]],
      color: color,
    };

    seriesData.push(obj);
  }

  return {
    chart: {
      type: "bar",
    },

    tooltip: { headerFormat: "" },

    credits: {
      enabled: false,
    },

    exporting: {
      enabled: false,
    },

    title: {
      text: "",
    },

    xAxis: {
      categories: xAxis,
      visible: false,
    },

    yAxis: {
      title: {
        text: "Severity",
      },
      visible: !!audits,
    },

    legend: {
      reversed: true,
    },

    plotOptions: {
      series: {
        stacking: "normal",
      },
    },

    series: seriesData,
  };
}
